import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import JSONInput from 'react-json-editor-ajrm';
import locale from 'react-json-editor-ajrm/locale/en';

import { Col, Container, Div, Row, Text } from '@pinpoint/design-kit';

import { appActions, clientsEditActions } from 'src/store/actions';
import { Message, Menu, Switch, Button } from 'src/components';
import Ubac from 'src/containers/ubac';
import CONSTANTS from 'src/store/constants';

const DELIMITER = '----';

/* eslint-disable react/prop-types */
function ConfigForm({ title, config, onSave }) {
  const configObject = config;
  const [state, setState] = useState({ ...configObject });

  const { handleSubmit } = useForm({});

  const handleChange = ({ jsObject, error }) => {
    if (error === false) setState(jsObject);
  };

  const onSubmit = useCallback(() => {
    onSave(state);
  }, [state, onSave]);

  return (
    <Div d="flex" flexDir="column">
      <Div textSize="subheader" textColor="tabSelected" d="flex" justify="space-between" align="center">
        {title}
        <Button buttonStyle="primary" m={{ r: '0rem' }} onClick={handleSubmit(onSubmit)}>
          <Text color="gray400">SAVE</Text>
        </Button>
      </Div>
      <Div d="flex" flexDir="column" m={{ y: '1rem' }}>
        <JSONInput
          id={title.toLowerCase().replace(/\s+/g, '-')}
          placeholder={state}
          theme="light_mitsuketa_tribute" // NOTE: update theme to colors and use onpoint theme colors
          locale={locale}
          // onKeyPressUpdate
          // waitAfterKeyPress={2000}
          width="100%"
          height="100%"
          onChange={handleChange}
          confirmGood={false}
        />
      </Div>
    </Div>
  );
}

function ConfigContainer({ option: { title: type, subTitle: scopeClientType }, tenant }) {
  const [compState, setCompState] = useState(CONSTANTS.COMPONENT.VALIDATION.PENDING);
  const dispatch = useDispatch();

  /* eslint-disable no-unused-vars */
  const {
    onboardingConfigTypes,
    fetchOnboardingConfigTypesStatus,
    clientOnboardingConfig,
    fetchClientOnboardingConfigStatus,
    onboardingConfigsL1,
    fetchOnboardingConfigsL1Status,
    onboardingConfigsL2,
    fetchOnboardingConfigsL2Status,
    onboardingConfigsL3,
    fetchOnboardingConfigsL3Status,
  } = useSelector(({ clientsEdit }) => clientsEdit);
  /* eslint-enable no-unused-vars */

  useEffect(() => {
    if (tenant && type) {
      dispatch(clientsEditActions.unset('onboardingConfigsL1', []));
      dispatch(clientsEditActions.unset('onboardingConfigsL2', []));
      dispatch(clientsEditActions.unset('onboardingConfigsL3', []));
      const l1Filter = [
        {
          name: 'type',
          op: '==',
          val: type,
        },
        {
          name: 'scope_client_type',
          op: 'is_null',
        },
        {
          name: 'scope_client_id',
          op: 'is_null',
        },
      ];
      dispatch(clientsEditActions.fetchOnboardingConfigsL1({ filters: l1Filter }));
      if (scopeClientType) {
        const l2Filter = [
          {
            name: 'type',
            op: '==',
            val: type,
          },
          {
            name: 'scope_client_type',
            op: '==',
            val: scopeClientType,
          },
          {
            name: 'scope_client_id',
            op: 'is_null',
          },
        ];
        dispatch(clientsEditActions.fetchOnboardingConfigsL2({ filters: l2Filter }));
        const l3Filter = [
          {
            name: 'type',
            op: '==',
            val: type,
          },
          {
            name: 'scope_client_type',
            op: '==',
            val: scopeClientType,
          },
          {
            name: 'scope_client_id',
            op: '==',
            val: tenant,
          },
        ];
        dispatch(clientsEditActions.fetchOnboardingConfigsL3({ filters: l3Filter }));
      } else {
        const l3Filter = [
          {
            name: 'type',
            op: '==',
            val: type,
          },
          {
            name: 'scope_client_type',
            op: 'is_null',
          },
          {
            name: 'scope_client_id',
            op: '==',
            val: tenant,
          },
        ];
        dispatch(clientsEditActions.fetchOnboardingConfigsL3({ filters: l3Filter }));
      }
    }
  }, [tenant, type, scopeClientType, dispatch]);

  useEffect(() => {
    if (
      fetchOnboardingConfigsL1Status === CONSTANTS.API.STARTED ||
      fetchOnboardingConfigsL2Status === CONSTANTS.API.STARTED ||
      fetchOnboardingConfigsL3Status === CONSTANTS.API.STARTED
    ) {
      setCompState(CONSTANTS.COMPONENT.VALIDATION.PENDING);
    }
    if (
      fetchOnboardingConfigsL1Status === CONSTANTS.API.SUCCEEDED &&
      fetchOnboardingConfigsL2Status !== CONSTANTS.API.STARTED &&
      fetchOnboardingConfigsL2Status !== CONSTANTS.API.FAILED &&
      fetchOnboardingConfigsL3Status === CONSTANTS.API.SUCCEEDED
    ) {
      setCompState(CONSTANTS.COMPONENT.VALIDATION.VALIDATED);
    }
    if (
      fetchOnboardingConfigsL1Status === CONSTANTS.API.FAILED ||
      fetchOnboardingConfigsL2Status === CONSTANTS.API.FAILED ||
      fetchOnboardingConfigsL3Status === CONSTANTS.API.FAILED
    ) {
      setCompState(CONSTANTS.COMPONENT.VALIDATION.ERROR);
    }
  }, [fetchOnboardingConfigsL1Status, fetchOnboardingConfigsL2Status, fetchOnboardingConfigsL3Status]);

  return (
    <Container w="100%" h="100%">
      <Switch w="100%" h="100%" status={compState}>
        <Div w="100%" h="100%" pos="relative" value={CONSTANTS.COMPONENT.VALIDATION.PENDING}>
          <Message />
        </Div>
        <Div value={CONSTANTS.COMPONENT.VALIDATION.ERROR}>
          <Div d="flex" align="center">
            <Div textSize="subheader" m={{ l: '0.5rem' }} textColor="tabSelected">
              System ran into an error when fetch the configuration. Please try again!
            </Div>
          </Div>
        </Div>
        <Div value={CONSTANTS.COMPONENT.VALIDATION.VALIDATED}>
          <Row>
            <Col size="4">
              {onboardingConfigsL3.length === 1 ? (
                <ConfigForm
                  title="Client scope"
                  config={onboardingConfigsL3[0].config}
                  onSave={(config) => {
                    const onbConfig = {
                      config,
                    };
                    dispatch(clientsEditActions.editOnboardingConfig(onboardingConfigsL3[0].id, onbConfig));
                  }}
                />
              ) : onboardingConfigsL3.length === 0 ? (
                <ConfigForm
                  title="Client scope"
                  config={{}}
                  onSave={(config) => {
                    const onbConfig = {
                      type,
                      scope_client_type: scopeClientType,
                      scope_client_id: tenant,
                      config,
                    };
                    dispatch(clientsEditActions.addOnboardingConfig(onbConfig));
                  }}
                />
              ) : (
                <Div d="flex" flexDir="column">
                  <Div textSize="subheader" textColor="tabSelected">
                    Client scope
                  </Div>
                  <Text m={{ y: '1rem' }}>Multiple configurations detected.</Text>
                </Div>
              )}
            </Col>
            <Col size="4">
              {scopeClientType ? (
                onboardingConfigsL2.length === 1 ? (
                  <ConfigForm
                    title="Type scope"
                    config={onboardingConfigsL2[0].config}
                    onSave={(config) => {
                      const onbConfig = {
                        config,
                      };
                      dispatch(clientsEditActions.editOnboardingConfig(onboardingConfigsL2[0].id, onbConfig));
                    }}
                  />
                ) : onboardingConfigsL2.length === 0 ? (
                  <ConfigForm
                    title="Type scope"
                    config="{}"
                    onSave={(config) => {
                      const onbConfig = {
                        type,
                        scope_client_type: scopeClientType,
                        config,
                      };
                      dispatch(clientsEditActions.addOnboardingConfig(onbConfig));
                    }}
                  />
                ) : (
                  <Div d="flex" flexDir="column">
                    <Div textSize="subheader" textColor="tabSelected">
                      Type scope
                    </Div>
                    <Text textColor="tabSelected" m={{ y: '1rem' }}>
                      Multiple configurations detected.
                    </Text>
                  </Div>
                )
              ) : (
                <Div d="flex" flexDir="column">
                  <Div textSize="subheader" textColor="tabSelected">
                    Type scope
                  </Div>
                  <Text textColor="tabSelected" m={{ y: '1rem' }}>
                    Type scope can&apos;t be added to this configuration.
                  </Text>
                </Div>
              )}
            </Col>
            <Col size="4">
              {onboardingConfigsL1.length === 1 ? (
                <ConfigForm
                  title="Base Config"
                  config={onboardingConfigsL1[0].config}
                  onSave={(config) => {
                    const onbConfig = {
                      config,
                    };
                    dispatch(clientsEditActions.editOnboardingConfig(onboardingConfigsL1[0].id, onbConfig));
                  }}
                />
              ) : onboardingConfigsL1.length === 0 ? (
                <ConfigForm
                  title="Basic Config"
                  config="{}"
                  onSave={(config) => {
                    const onbConfig = {
                      type,
                      config,
                    };
                    dispatch(clientsEditActions.addOnboardingConfig(onbConfig));
                  }}
                />
              ) : (
                <Div d="flex" flexDir="column">
                  <Div textSize="subheader" textColor="tabSelected">
                    Basic Config
                  </Div>
                  <Text m={{ y: '1rem' }}>Multiple configurations detected.</Text>
                </Div>
              )}
            </Col>
          </Row>
        </Div>
      </Switch>
    </Container>
  );
}

function OnboardingConfigs({ tenant }) {
  const [compState, setCompState] = useState(CONSTANTS.COMPONENT.VALIDATION.PENDING);

  const dispatch = useDispatch();

  /* eslint-disable no-unused-vars */
  const {
    onboardingConfigTypes,
    fetchOnboardingConfigTypesStatus,
    clientOnboardingConfig,
    fetchClientOnboardingConfigStatus,
    onboardingConfigs,
    fetchOnboardingConfigsStatus,
    editOnboardingConfigStatus,
    addOnboardingConfigStatus,
  } = useSelector(({ clientsEdit }) => clientsEdit);
  /* eslint-enable no-unused-vars */

  // Fetch information about the onboarding configs
  useEffect(() => {
    dispatch(clientsEditActions.fetchOnboardingConfigTypes());
  }, [dispatch]);

  useEffect(() => {
    if (fetchOnboardingConfigTypesStatus === CONSTANTS.API.SUCCEEDED) {
      if (onboardingConfigTypes.length > 0) {
        setCompState(CONSTANTS.COMPONENT.VALIDATION.VALIDATED);
      } else {
        setCompState(CONSTANTS.COMPONENT.VALIDATION.FAILED);
      }
    }
    if (fetchOnboardingConfigTypesStatus === CONSTANTS.API.FAILED) {
      setCompState(CONSTANTS.COMPONENT.VALIDATION.ERROR);
    }
  }, [fetchOnboardingConfigTypesStatus, onboardingConfigTypes]);

  useEffect(() => {
    if (editOnboardingConfigStatus === CONSTANTS.API.FAILED) {
      dispatch(appActions.enqueueNotification('There was a problem trying to update the configuration'));
    }
    if (editOnboardingConfigStatus === CONSTANTS.API.SUCCEEDED) {
      dispatch(appActions.enqueueNotification('Configuration has been updated', { autoClose: true }));
    }
  }, [editOnboardingConfigStatus, dispatch]);

  useEffect(() => {
    if (addOnboardingConfigStatus === CONSTANTS.API.FAILED) {
      dispatch(appActions.enqueueNotification('There was a problem trying to update the configuration'));
    }
    if (addOnboardingConfigStatus === CONSTANTS.API.SUCCEEDED) {
      dispatch(appActions.enqueueNotification('Configuration has been updated', { autoClose: true }));
    }
  }, [addOnboardingConfigStatus, dispatch]);

  return (
    <Ubac users={CONSTANTS.ENG_TEAM}>
      <Container>
        <Div bg="white" m={{ y: '1rem' }} p={{ x: '1.5rem', y: '1.25rem' }} rounded="md">
          <Text textSize="title" textColor="tabSelected" m={{ y: '0.5rem' }}>
            Configuration
          </Text>
          <Switch status={compState}>
            <Div value={CONSTANTS.COMPONENT.VALIDATION.PENDING}>
              <Div d="flex" align="center">
                <Div textSize="subheader" m={{ l: '0.5rem' }} textColor="tabSelected">
                  Loading... Please wait!
                </Div>
              </Div>
            </Div>
            <Div value={CONSTANTS.COMPONENT.VALIDATION.FAILED}>
              <Div d="flex" align="center">
                <Div textSize="subheader" m={{ l: '0.5rem' }} textColor="tabSelected">
                  System has no configuration. Please try again!
                </Div>
              </Div>
            </Div>
            <Div value={CONSTANTS.COMPONENT.VALIDATION.ERROR}>
              <Div d="flex" align="center">
                <Div textSize="subheader" m={{ l: '0.5rem' }} textColor="tabSelected">
                  System ran into an error when fetch the configuration. Please try again!
                </Div>
              </Div>
            </Div>
            <Div value={CONSTANTS.COMPONENT.VALIDATION.VALIDATED}>
              <Menu
                options={onboardingConfigTypes.map(({ type, scope_client_type: scopeClientType }) => ({
                  title: type,
                  subTitle: scopeClientType,
                  key: `${type}${DELIMITER}${scopeClientType}`,
                }))}
                Component={ConfigContainer}
                componentProps={{ tenant }}
                leftColSize="3"
                rightColSize="9"
              />
            </Div>
          </Switch>
        </Div>
      </Container>
    </Ubac>
  );
}
/* eslint-enable react/prop-types */

export default OnboardingConfigs;
